import { useState } from 'react';
import ConfirmationDialog from '@/components/shared/ConfirmationDialog';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/NewButton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  formatDocketDate,
  getDocketCounts,
  isAwaitingFirstSync,
  lifecycleStatusTone,
  syncStatusLabel,
  syncStatusPulses,
  syncStatusTone,
} from './format';
import { StatusPill } from './StatusPill';
import type { Docket } from './schema';

interface DocketHeaderProps {
  docket: Docket;
  onUnlink?: () => void;
}

interface FieldProps {
  label: string;
  value: string | null | undefined;
}

const Field = ({ label, value }: FieldProps) => (
  <div className="flex flex-col gap-1">
    <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
      {label}
    </span>
    <span className="text-sm font-medium text-foreground">{value || '—'}</span>
  </div>
);

const pillClassName = 'px-1.5 py-px text-[11px]';

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

const DocketHeader = ({ docket, onUnlink }: DocketHeaderProps) => {
  const counts = getDocketCounts(docket);
  const awaitingFirstSync = isAwaitingFirstSync(docket);
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  // Show the provider's raw status label when present, but key the pill colour
  // off the normalized lifecycle so it doesn't depend on raw provider text.
  const lifecycleStatus = docket.lifecycleStatus ?? 'unknown';
  const statusLabel =
    docket.status ?? (lifecycleStatus !== 'unknown' ? capitalize(lifecycleStatus) : null);

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between p-6 md:py-8">
        <div className="flex min-w-0 flex-col gap-1.5">
          <h2 className="text-xl font-semibold leading-tight tracking-tight text-foreground">
            {docket.caseName}
          </h2>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <span className="font-mono text-xs font-medium text-foreground/80">
              {docket.caseNumber}
            </span>
            <span className="text-muted-foreground/40">·</span>
            <span>{docket.court.name}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {statusLabel && (
            <StatusPill
              label={statusLabel}
              tone={lifecycleStatusTone(lifecycleStatus)}
              className={pillClassName}
            />
          )}
          {docket.syncError?.message ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <StatusPill
                      label={syncStatusLabel(docket.syncStatus)}
                      tone={syncStatusTone(docket.syncStatus)}
                      pulse={syncStatusPulses(docket.syncStatus)}
                      className={pillClassName}
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[240px] text-xs font-normal">
                  {docket.syncError.message}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <StatusPill
              label={syncStatusLabel(docket.syncStatus)}
              tone={syncStatusTone(docket.syncStatus)}
              pulse={syncStatusPulses(docket.syncStatus)}
              className={pillClassName}
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-5 p-6 sm:grid-cols-3 lg:grid-cols-4">
        <Field label="Date filed" value={formatDocketDate(docket.dateFiled)} />
        <Field label="Assigned judge" value={docket.assignedJudge} />
        <Field label="Nature of suit" value={docket.natureOfSuit} />
        <Field label="Court system" value={docket.court.system} />
        <Field label="Cause" value={docket.cause} />
        <Field label="Case type" value={docket.caseType} />
        <Field label="Area of law" value={docket.areaOfLaw} />
        <Field label="Jurisdiction type" value={docket.jurisdictionType} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-6 py-3">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {awaitingFirstSync ? (
            <span className="text-xs text-muted-foreground">
              Fetching docket entries — this may take a few minutes.
            </span>
          ) : (
            <>
              <Badge variant="secondary" className="font-medium">
                {counts.entries} Entries
              </Badge>
              <Badge variant="secondary" className="font-medium">
                {counts.documents} Documents
              </Badge>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {docket.sourceUrl && (
            <Button
              variant="ghost"
              size="sm"
              text="Source"
              href={docket.sourceUrl}
              target="_blank"
            />
          )}
          {onUnlink && (
            <Button
              variant="ghost"
              size="sm"
              text="Unlink"
              onClick={() => setConfirmUnlink(true)}
              className="text-muted-foreground hover:text-destructive"
            />
          )}
        </div>
      </div>

      {onUnlink && (
        <ConfirmationDialog
          title="Unlink this docket?"
          visible={confirmUnlink}
          onConfirm={onUnlink}
          onCancel={() => setConfirmUnlink(false)}
          confirmText="Unlink"
        >
          This removes the link between{' '}
          <span className="font-medium text-foreground">{docket.caseName}</span> and this matter. The
          docket record itself is not deleted, and you can link it again later.
        </ConfirmationDialog>
      )}
    </Card>
  );
};

export default DocketHeader;
